<script lang="ts" setup>
// Global Libaries
import { computed, ref, inject, onUnmounted, onMounted, watch, nextTick, PropType } from "vue";
import { useLocalStorage } from "@vueuse/core";
import { useAdTracker } from '@/composables/ad-tracker';

// Types
import { AdType, SiteLocation } from "@tmi/types/entities";
import type { DynamicAd } from "@tmi/types/entities";
import { adServiceSymbol } from "@/plugins/AdServicePlugin";

// Composables
import type { AdService } from "@/composables/ad-service";
import { reportUserAdClick } from "@/composables/user-activity-monitor";

type AdTypeValue = typeof AdType[keyof typeof AdType];
type SiteLocationValue = typeof SiteLocation[keyof typeof SiteLocation];

const adTracker = useAdTracker();
const adService = inject<AdService>( adServiceSymbol ) as AdService;

const props = defineProps({
  adType: {
    type: String as PropType<AdTypeValue>,
    required: false,
    default: AdType.Popup // Default to Popup if not provided
  },
  siteLocation: {
    type: String as PropType<SiteLocationValue>,
    required: false,
    default: SiteLocation.Dashboard // Default to Dashboard if not provided
  },
  displayProducts: {
    type: Array as PropType<string[] | undefined>,
    required: false,
    default: undefined,
  },
  // Every ad on screen is normally a different one. With this set, an inline slot that finds every
  // eligible ad already taken shows one again instead of staying empty.
  allowRepeat: {
    type: Boolean,
    required: false,
    default: false
  }
} );

const availableAdsBase = computed<DynamicAd[]>( () => {
  return adService.adsFilteredByProductContextAdTypeAndSiteLocation( {
    productShortCodes: props.displayProducts,
    adType: props.adType,
    siteLocation: props.siteLocation
  } );
} );

const ad = ref<DynamicAd | null>( null );
const assignedAdId = ref<string | null>( null ); // Track the ID assigned to *THIS* instance
const showPopup = ref( false );
const popupTimeoutId = ref<ReturnType<typeof setTimeout> | null>( null );

const MAX_AD_CLICKS = 10;
const POPUP_DELAY = 60;
const POPUP_COOLDOWN_HOURS = 0.15;
const POPUP_COOLDOWN_MILLISECONDS = POPUP_COOLDOWN_HOURS * 60 * 60 * 1000;

const adClicks = useLocalStorage<Record<string, number>>( "adClicks", {} );
const lastPopupClick = useLocalStorage<number | null>('lastPopupClick', null);


// Function to clear the popup timeout and reset visibility
const clearPopupTimeout = () => {
  if ( popupTimeoutId.value ) {
    clearTimeout( popupTimeoutId.value );
    popupTimeoutId.value = null;
  }
  showPopup.value = false;
};

const recordPopupInteraction = () => {
  if ( props.adType === AdType.Popup ) {
    lastPopupClick.value = Date.now();
  }
};

// Function to select ad
const selectNextAvailableAd = () => {
  // Clear any existing popup timeout before selecting a new ad
  clearPopupTimeout();

  if ( props.adType === AdType.Popup ) {
    const lastInteraction = lastPopupClick.value;

    if ( lastInteraction && ( Date.now() - lastInteraction < POPUP_COOLDOWN_MILLISECONDS ) ) {
      if ( assignedAdId.value ) {
        adTracker.releaseAdId( assignedAdId.value );
      }

      ad.value = null;
      assignedAdId.value = null;
      return;
    }
  }

  // Release previous ad ID if held by this instance *before* selecting a new one
  if ( assignedAdId.value ) {
    adTracker.releaseAdId( assignedAdId.value );
    assignedAdId.value = null;
    ad.value = null;
  }

  const activeAds = availableAdsBase.value.filter(
    ( ad: DynamicAd ) =>
      ad.contentfulId &&
      ( adClicks.value[ ad.contentfulId ] || 0 ) < MAX_AD_CLICKS
  );

  if ( activeAds.length === 0 ) {
    ad.value = null;
    assignedAdId.value = null;
    return;
  }

  const sortedAds = [ ...activeAds ].sort( ( a, b ) =>
    a.priority === b.priority
      ? Math.random() - 0.5     // random order within the same priority
      : a.priority - b.priority // normal ascending priority
  );

  let successfullyAssigned = false;
  for ( const potentialAd of sortedAds ) {
    const potentialAdId = potentialAd.contentfulId;

    if ( potentialAdId ) {
      // Attempt to reserve using the composable function
      if ( adTracker.reserveAdId( potentialAdId ) ) {
        assignedAdId.value = potentialAdId;
        ad.value = potentialAd;
        successfullyAssigned = true;

        // Delay Logic
        if (props.adType === AdType.Popup && ad.value) {
          const delayMilliseconds = ( ad.value.displayDelaySec || POPUP_DELAY ) * 1000;

          popupTimeoutId.value = setTimeout( () => {
            if ( ad.value?.contentfulId === potentialAdId ) {
                showPopup.value = true;
            }
          }, delayMilliseconds );
        }
        // End Delay Logic
        break;
      }
    }
  }

  // If loop finished without assigning, ensure state is null.
  if ( !successfullyAssigned ) {
    assignedAdId.value = null;
    // Every eligible ad is already on screen. A slot that allows repeats shows the first one again,
    // unreserved, so it never releases a reservation another slot holds.
    ad.value = props.allowRepeat ? sortedAds[ 0 ] : null;
  }
};

const shouldApplyCardStyle = () => {
  if ( !ad.value || !ad.value.contentfulId ) return false;
  return ( ad.value.body && !ad.value.imageUri );
};

const hideAd = () => {
  clearPopupTimeout();
  recordPopupInteraction();

  if ( ad.value ) {
    const adId = ad.value.contentfulId;

    const currentClicks = ( adClicks.value[ adId ] || 0 ) + 1;
    adClicks.value[ adId ] = currentClicks;
    adClicks.value = { ...adClicks.value };
  }

  if ( assignedAdId.value ) {
    adTracker.releaseAdId( assignedAdId.value );
    assignedAdId.value = null;
  }

  ad.value = null;
};

const handleAdClick = () => {
  if (!ad.value || !ad.value.contentfulId) return;
  const adId = ad.value.contentfulId;

  const currentClicks = ( adClicks.value[ adId ] || 0 ) + 1;
  adClicks.value[ adId ] = currentClicks;
  adClicks.value = { ...adClicks.value };

  clearPopupTimeout();
  recordPopupInteraction();

  if ( ad.value.url ) {
    try {
      const urlObj = new URL( ad.value.url );
      const utmAd = urlObj.searchParams.get( "utm_ad" );

      if ( utmAd ) {
        reportUserAdClick( utmAd );
      }
    } catch ( e ) {
      // console.error( "Invalid ad URL:", e );
    }
    window.open( ad.value.url, "_blank" );
  }

  // If max clicks reached, hide ad and release its ID.
  if ( currentClicks >= MAX_AD_CLICKS ) {
    adTracker.releaseAdId( assignedAdId.value );
    ad.value = null;
    assignedAdId.value = null;
  }
};

// The popup is a modal <dialog>: the browser draws it in the top layer, in front of everything on the
// page whatever its z-index, including PrimeVue overlays such as the article slide-over.
const popupDialog = ref< HTMLDialogElement >();
watch( [ showPopup, popupDialog ], ( [ show, dialog ] ) => {
  if ( !dialog ) {
    return;
  }

  if ( show && !dialog.open ) {
    dialog.showModal();
  } else if ( !show && dialog.open ) {
    dialog.close();
  }
} );

// Lifecycle Hooks
onMounted( () => {
  nextTick( () => {
    if ( availableAdsBase.value.length > 0 ) {
      selectNextAvailableAd();
    }
  } );
} );

onUnmounted( () => {
  clearPopupTimeout();
  adTracker.releaseAdId( assignedAdId.value );

  assignedAdId.value = null;
  ad.value = null;
} );

watch( availableAdsBase, ( newAds: DynamicAd[], oldAds: DynamicAd[] ) => {
  // Only run selection if the list has actually become populated
  // and *this* component isn't already showing an ad.
  if ( newAds.length > 0 && !ad.value ) {
    nextTick( () => {
      selectNextAvailableAd();
    });
  }
}, { immediate: false } );
</script>
<template>
<div v-if="ad">

  <div v-if="props.adType === 'banner'" :class="['mx-auto mt-2 max-w-[800px]', { 'card p-4': shouldApplyCardStyle() }]">
    <div v-if="shouldApplyCardStyle()" class="flex items-center justify-between w-full">
      <div class="w-4/5" v-html="ad.body"></div>
      <div class="w-1/5 flex justify-end">
        <button @click="handleAdClick()" class="btn btn-primary w-full">
          {{ ad.buttonText ?? 'Click Here!'}}
        </button>
      </div>
    </div>
    <button v-else type="button" @click="handleAdClick()" class="mt-2 cursor-pointer block w-full">
      <img class="w-full h-auto rounded-md object-contain" :src="ad.imageUri" alt="Banner Ad"/>
    </button>
  </div>


  <!-- Opened with showModal() once the delay passes (see popupDialog). Escape hides it like "Hide" does and
       goes no further: PrimeVue's slide-over closes on any Escape keydown that reaches the document. -->
  <dialog
    v-else-if="props.adType === 'popup'"
    ref="popupDialog"
    aria-label="Advertisement"
    class="m-0 size-full max-h-none max-w-none border-0 bg-transparent p-0 backdrop:bg-black/60"
    @cancel.prevent="hideAd()"
    @keydown.esc.stop
  >
    <!-- The backdrop's click-to-dismiss mirrors the Hide button inside. -->
    <div @click="hideAd()" data-click-mirror="Hide button" class="flex size-full items-center justify-center">
      <!-- The card's click mirrors its call-to-action button, whose click bubbles here. -->
      <div v-if="shouldApplyCardStyle()" @click="handleAdClick()" data-click-mirror="call-to-action button" class="card max-w-screen-sm bg-base-100 p-5 pb-1 -mt-20 cursor-pointer">
          <div v-html="ad.body"></div>
          <button class="btn btn-primary w-full mt-5 text-lg">
            {{ ad.buttonText ?? 'Click here' }}
          </button>
        <!-- .stop: hiding must not also reach the card's ad click (today hideAd clears the ad first, so it was already a no-op). -->
        <button type="button" @click.stop="hideAd()" class="block w-full cursor-pointer text-muted text-center my-2 text-sm">Hide</button>
      </div>
      <div v-else class="max-w-screen-sm cursor-pointer">
        <button type="button" @click="handleAdClick()" class="block w-full cursor-pointer">
          <img :src="ad.imageUri" class="max-h-[600px] w-full h-auto rounded-xs object-contain" alt="Popup Ad"/>
        </button>
        <button type="button" @click.stop="hideAd()" class="block w-full cursor-pointer text-white text-center text-md my-2">Hide</button>
      </div>
    </div>
  </dialog>

  <div v-else-if="props.adType === 'side-right'" class="mx-auto cursor-pointer">
    <div v-if="shouldApplyCardStyle()" class="card p-4">
      <!-- The card's click mirrors its call-to-action button, whose click bubbles here. -->
      <div @click="handleAdClick()" data-click-mirror="call-to-action button">
        <div v-html="ad.body"></div>
        <button class="btn btn-primary w-full mt-5 text-lg">{{ ad.buttonText ?? 'Click here' }}</button>
      </div>
    </div>
    <div v-else>
      <button type="button" @click="handleAdClick()" class="block w-full cursor-pointer">
        <img class="max-w-screen-md w-4/5 mx-auto h-auto rounded-xs object-contain" :src="ad.imageUri" alt="Side Ad"/>
      </button>
    </div>
  </div>
</div>
</template>
